import React, { FC } from 'react';
import { connect } from 'react-redux';

import { CurrencyCode } from '../../../../../../api/services/site-configs/types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState } from 'app/redux/types';

import { StakeType } from 'app/lib/staking/constants';

import selectCurrency from '../../../../../../redux/selectors/сurrency';
import { AccountType, AmountValidationResult, SubmissionState } from '../../../../constants';
import { RecordOfEntryAmountType, RecordOfEntryErrorType } from '../../../../records';
import { selectHasEnoughMoney } from '../../../../selectors';
import { selectBets, selectTotalAmountActive } from '../../../../selectors/entries';
import { selectCurrentStakeType } from '../../../../selectors/stakeTypes';
import {
  selectEntryAmountValidation,
  selectIsOnlyAcceptAmountSingleError,
  selectSingleBetsInfo,
} from '../../../../selectors/stakeTypes/single';
import { selectSubmission } from '../../../../selectors/submission';
import type { PledgeStakeDAO } from '../../../../types/backend/paymentOptions';
import Error from '../../../EntryComponents/Error';
import { getPresetsAndFreebetsBar } from '../../../FooterBar';
import MinLimitError from '../../../MinLimitError';
import NoMoneyLink from '../../../NoMoneyLink';
import TextWithAmountBar from '../../../TextWithAmountBar';
import PledgeStakeMaxWinning from '../PledgeStake/MaxWinning';
import PledgeStakeTextBar from '../PledgeStake/TextBar';

import dictionary from './Footer.dictionary.json';

// ---

type StoreProps = {
  disabled: boolean;
  minValue: number;
  maxValue: number;
  freeBet: FreeBet | null;
  pledgeStake: PledgeStakeDAO | null;
  maxWinning: number;
  freeBetMaxWinning: number | null;
  amount?: RecordOfEntryAmountType | null;
  showNoMoneyError: boolean;
  showMinLimitError: boolean;
  showMaxLimitError: boolean;
  isOnlyAcceptSingleError: boolean;
  error?: RecordOfEntryErrorType | null;
  currency: CurrencyCode;
};

type EntryFooterContentProps = {
  betId: number;
};

const EntryFooterContent: FC<EntryFooterContentProps & StoreProps> = ({
  betId,
  disabled,
  minValue,
  maxValue,
  freeBet,
  pledgeStake,
  maxWinning,
  freeBetMaxWinning,
  amount,
  showNoMoneyError,
  showMinLimitError,
  showMaxLimitError,
  isOnlyAcceptSingleError,
  error,
  currency,
}) => {
  const amountValue = amount?.value;

  if (!disabled) {
    if (isOnlyAcceptSingleError) {
      return (
        <Error error={error}
               currency={currency} />
      );
    }

    if (showNoMoneyError) {
      return (
        <NoMoneyLink />
      );
    }

    if (showMaxLimitError && pledgeStake && amount?.account === AccountType.PLEDGE_STAKE_ACCOUNT) {
      return (
        <PledgeStakeMaxWinning hasError
                               betId={betId}
                               value={maxValue} />
      );
    }

    if (showMinLimitError) {
      return (
        <MinLimitError betId={betId}
                       value={minValue} />
      );
    }
  }

  if (freeBet && freeBetMaxWinning) {
    return (
      <TextWithAmountBar value={freeBetMaxWinning}
                         label={dictionary.label} />
    );
  }

  if (amountValue) {
    return (
      <>
        {pledgeStake && (
          <PledgeStakeTextBar amountValue={amountValue}
                              value={pledgeStake} />
        )}
        <TextWithAmountBar value={maxWinning}
                           data-test="entry-maxWinning"
                           label={dictionary.label} />
      </>
    );
  }

  if (pledgeStake) {
    return (
      <PledgeStakeMaxWinning betId={betId}
                             value={maxValue} />
    );
  }

  if (amount?.account === AccountType.MAIN_ACCOUNT) {
    const PresetsAndFreebetsBar = getPresetsAndFreebetsBar('entry');
    return (
      <PresetsAndFreebetsBar betId={betId} />
    );
  }

  return null;
};

const mapStateToProps = (state: ReduxState, { betId }: EntryFooterContentProps): StoreProps => {
  const betInfo = selectSingleBetsInfo(state).get(betId);
  const entries = selectBets(state);
  const entry = entries.get(betId);

  const singleAmount = betInfo?.get('amount');
  const minValue = betInfo?.limits.min || 0;
  const maxValue = betInfo?.limits.max || 0;
  const entryPrice = entry?.price || 0;
  const pledgeStake = betInfo?.pledgeStake || null;
  const pledgeStakeMaxWinning = pledgeStake?.maxWinning || 0;

  const amount = (singleAmount?.selector) ? singleAmount.selector(state) : singleAmount;
  const freeBet = betInfo?.freeBet || null;
  const hasEnoughMoney = selectHasEnoughMoney(state);
  const showNoMoneyError = !hasEnoughMoney && !selectTotalAmountActive(state) && entries.size <= 1;

  const validation = selectEntryAmountValidation(state, betId);

  const isSingle = selectCurrentStakeType(state).has(StakeType.SINGLE);
  const showMinLimitError = isSingle && validation === AmountValidationResult.MIN_NOT_REACHED;
  const showMaxLimitError = validation === AmountValidationResult.MAX_EXCEEDED;

  return {
    disabled: selectSubmission(state) !== SubmissionState.AWAITING,
    freeBet,
    pledgeStake: betInfo?.pledgeStake || null,
    maxWinning: amount?.value
      ? parseFloat(amount.value) * entryPrice
      : 0,
    freeBetMaxWinning: freeBet?.amount
      ? (freeBet.amount * entryPrice) - freeBet.amount
      : null,
    amount,
    minValue,
    maxValue: pledgeStakeMaxWinning > maxValue
      ? maxValue
      : pledgeStakeMaxWinning,
    showNoMoneyError,
    showMinLimitError,
    showMaxLimitError,
    isOnlyAcceptSingleError: selectIsOnlyAcceptAmountSingleError(state, betId),
    error: betInfo?.get('error'),
    currency: selectCurrency(state),
  };
};

export default connect(mapStateToProps)(EntryFooterContent);
